<script setup lang="ts">
import BaseIcon from '~/components/foundations/BaseIcon.vue'
import type { IconType } from '~/components/foundations/IconType'
import TextLabel from '~/components/foundations/TextLabel.vue'

type Color = 'neutral-default' | 'brand-strong'
type Props = {
  disabled?: boolean
  text: string
  color?: Color
  selected?: boolean
  icon?: {
    type: IconType | undefined
    position: 'prefix' | 'suffix' | 'none'
    rotate90?: boolean
  }
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  color: 'neutral-default',
  selected: false,
  size: 'medium',
  icon: () => ({
    type: undefined,
    position: 'none',
    rotate90: false,
  }),
})

const emit = defineEmits<{
  (e: 'click'): void
}>()
</script>

<template>
  <button
    :disabled="disabled"
    class="text-button"
    :class="{ [color]: true, [size]: true, [props.icon.position]: true, selected }"
    @click="emit('click')"
  >
    <template v-if="props.icon.type && props.icon.position === 'prefix'">
      <BaseIcon
        :type="props.icon.type"
        :color="color"
        :size="18"
        :rotate-90="props.icon.rotate90"
      />
    </template>
    <TextLabel size="large" :color="color">{{ text }}</TextLabel>
    <template v-if="props.icon.type && props.icon.position === 'suffix'">
      <BaseIcon
        :type="props.icon.type"
        :color="color"
        :size="18"
        :rotate-90="props.icon.rotate90"
      />
    </template>
  </button>
</template>

<style scoped lang="scss">
.text-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: spacingToken.$spacing-xxs;

  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  min-width: 88px;

  &.small {
    height: 32px;
  }
  &.medium {
    height: 40px;
  }
  &.large {
    height: 48px;
  }

  &.none {
    padding: 0 spacingToken.$spacing-m;
  }
  &.prefix {
    padding-left: spacingToken.$spacing-s;
    padding-right: spacingToken.$spacing-m;
  }
  &.suffix {
    padding-left: spacingToken.$spacing-m;
    padding-right: spacingToken.$spacing-s;
  }

  &.neutral-default {
    &:not(:disabled):hover {
      background-color: var(--semantic-tokens-neutral-xweak-surface);
    }

    &:not(:disabled).selected {
      background-color: var(--semantic-tokens-neutral-xweak-surface);
    }
  }

  &.brand-strong {
    svg {
      fill: var(--semantic-tokens-brand-strong-text);
    }
    &:not(:disabled):hover {
      background-color: var(--semantic-tokens-brand-weak-surface);
    }

    &:not(:disabled).selected {
      background-color: var(--semantic-tokens-brand-weak-surface);
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
}
</style>
